import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { CompanyService } from '@services/http/company.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { Option } from '@interfaces/common/option';
import { finalize, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-set-primary-member-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './set-primary-member-modal.component.html',
  styleUrls: ['./set-primary-member-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetPrimaryMemberModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef!: DialogComponent;
  @Output() primaryMemberSet = new EventEmitter<void>();

  companyOptions: Option[] = [];
  selectedCompanyId: string | null = null;

  private groupId!: string;
  private existingMemberIds: string[] = [];

  constructor(
    translateService: TranslateService,
    private readonly groupsService: EconomicGroupsHttpService,
    private readonly companyService: CompanyService,
    private readonly toast: ToastHelperService,
    private readonly formErrorHandler: FormErrorHandlerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
    super(translateService);
  }

  open(groupId: string, existingMemberIds: string[] = []): void {
    this.groupId = groupId;
    this.existingMemberIds = existingMemberIds;
    this.selectedCompanyId = null;
    this.loadCompanies();
    this.dialogRef.open('', '', '', 'Cancelar', '', 'set-primary-modal', false, false);
  }

  isFormValid(): boolean {
    return !!this.selectedCompanyId;
  }

  submitAction(): void {
    if (!this.isFormValid()) {
      this.toast.showToastError('Selecione uma empresa.');
      return;
    }

    this.isLoading = true;

    this.groupsService
      .setPrimaryMember(this.groupId, { companyId: this.selectedCompanyId! })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess('Empresa principal definida.');
          this.primaryMemberSet.emit();
          this.dialogRef.close();
        },
        error: err => this.formErrorHandler.showToast(err, 'Erro ao definir empresa principal.'),
      });
  }

  private loadCompanies(): void {
    this.companyService
      .getAllCompanys()
      .pipe(
        map(res =>
          (res?.data ?? [])
            .filter(
              (c): c is typeof c & { id: string } =>
                !!c.id && !this.existingMemberIds.includes(c.id!)
            )
            .map(c => ({ label: `${c.corporateName} — ${c.cnpj}`, value: c.id! }))
        ),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(opts => {
        this.companyOptions = opts;
        this.cdr.markForCheck();
      });
  }
}
